import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'

import { useCashier, useCashierBalance } from '@/context/CashierProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { getTokenByCurrency, getTokenByDenom } from '@/services/token-service'
import ChevronDownIcon from '@/public/assets/icons/game-zone/chevron-down.svg'
import { GameZoneModal } from '@/components/molecules/GameZoneModal/GameZoneModal'
import { usePriceStore } from '@/hooks/usePriceStore'
import { GameZoneAmountInput } from '@/components/molecules/GameZoneAmountInput/GameZoneAmountInput'
import { GameZoneNotice } from '@/components/molecules/GameZoneNotice/GameZoneNotice'
import { useTranslation } from 'next-i18next/pages'

export const CASHIER_TRANSACTION_SUCCESS_EVENT = 'cashier:transaction-success'

export interface CashierModalProps {
  embedded?: boolean
  onClose: () => void
  currency: SupportedCurrencies
  mode: 'deposit' | 'withdraw'
  exchangeRate?: number | string | null
  onSuccess?: (event: CashierTransactionSuccessEvent) => void
}

export interface CashierTransactionSuccessEvent {
  mode: 'deposit' | 'withdraw'
  currency: SupportedCurrencies
  amount: string
}

const formatAmount = (value: number, maximumFractionDigits = 6) =>
  value.toLocaleString(undefined, { maximumFractionDigits })

export const CashierModal: React.FC<CashierModalProps> = ({
  onClose,
  currency,
  mode,
  exchangeRate,
  onSuccess,
  embedded = false,
}) => {
  const { t } = useTranslation('common')
  const { deposit, withdraw, isProcessing, canDeposit, canWithdraw } = useCashier()
  const { getPrice } = usePriceStore()
  const balances = useBalanceStore((state) => state.balance)
  const initialCurrency = currency === SupportedCurrencies.SHU ? SupportedCurrencies.USDC : currency
  const availableCurrencies = useMemo(() => {
    const currencies = Object.keys(balances)
      .map((balanceDenom) => getTokenByDenom(balanceDenom)?.currency)
      .filter((value): value is SupportedCurrencies => Boolean(value) && value !== SupportedCurrencies.SHU)

    return Array.from(new Set([initialCurrency, ...currencies]))
  }, [balances, initialCurrency])
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrencies>(initialCurrency)
  const [amount, setAmount] = useState('')
  const selectedToken = getTokenByCurrency(selectedCurrency)
  const selectedDenom = selectedToken.denom
  const { gameBalance, walletBalance } = useCashierBalance(selectedDenom)
  const maxAmount = mode === 'deposit' ? walletBalance : gameBalance
  const currencyLabel = selectedCurrency.toUpperCase()
  const tokenName = selectedToken.name
  const atomicSessionRate = Number(exchangeRate)
  const sessionRatePerToken = atomicSessionRate * 10 ** selectedToken.decimals
  const price =
    selectedCurrency === currency && Number.isFinite(sessionRatePerToken) && sessionRatePerToken > 0
      ? sessionRatePerToken
      : getPrice(selectedCurrency)
  const toUsd = (value: number) => value * price
  const showUsdEquivalent = selectedCurrency !== currency || atomicSessionRate !== 1

  useEffect(() => {
    setSelectedCurrency(initialCurrency)
    setAmount('')
  }, [initialCurrency, mode])

  const inputAmount = useMemo(() => {
    if (!amount) return new TokenMoney(0, selectedDenom)
    return TokenMoney.fromHuman(amount, selectedDenom)
  }, [amount, selectedDenom])

  const canExecute =
    mode === 'deposit' ? canDeposit(inputAmount, selectedDenom) : canWithdraw(inputAmount, selectedDenom)
  const otherGameBalances = useMemo(
    () =>
      Object.entries(balances).filter(
        ([balanceDenom, balance]) => balanceDenom !== selectedDenom && balance.game_balance.chainValue.gt(0)
      ),
    [balances, selectedDenom]
  )

  const handleCurrencyChange = (nextCurrency: SupportedCurrencies) => {
    setSelectedCurrency(nextCurrency)
    setAmount('')
  }

  const handleExecute = async () => {
    if (mode === 'deposit') {
      for (const [balanceDenom, balance] of otherGameBalances) {
        const withdrawn = await withdraw(balance.game_balance, balanceDenom)
        if (!withdrawn) {
          return
        }
      }
    }

    const success =
      mode === 'deposit'
        ? await deposit(inputAmount, selectedDenom)
        : await withdraw(inputAmount, selectedDenom)

    if (success) {
      const event: CashierTransactionSuccessEvent = {
        mode,
        currency: selectedCurrency,
        amount,
      }

      window.dispatchEvent(new CustomEvent(CASHIER_TRANSACTION_SUCCESS_EVENT, { detail: event }))
      onSuccess?.(event)
      setAmount('')
      onClose()
    }
  }

  const title = t(mode === 'deposit' ? 'game-zone.add-to-balance' : 'game-zone.withdraw-to-wallet')
  const availableBalance = mode === 'deposit' ? walletBalance : gameBalance

  return (
    <GameZoneModal
      embedded={embedded}
      variant={mode}
      title={title}
      description={
        mode === 'deposit' ? t('game-zone.deposit-description') : t('game-zone.withdraw-description')
      }
      isBusy={isProcessing}
      onClose={onClose}
    >
      <VStack align="stretch" spacing={1.5}>
        <Text fontSize="12px" fontWeight={700} lineHeight={1} color="ninja.200">
          {t('game-zone.game-token')}
        </Text>
        {mode === 'deposit' ? (
          <HStack
            minH="64px"
            px={4}
            py={3}
            bg="ninja.500"
            borderRadius="12px"
            spacing={3}
            justify="space-between"
          >
            <HStack spacing={2} minW={0} flex={1}>
              <Box boxSize="24px" flexShrink={0}>
                {selectedToken.icon('24px')}
              </Box>
              <Text fontSize="15px" fontWeight={700} overflowWrap="anywhere">
                {tokenName}
              </Text>
            </HStack>
            <VStack align="flex-end" spacing={1} minW={0} maxW="60%" textAlign="right">
              <Text color="ninja.100" fontSize="12px" fontWeight={600} lineHeight={1.3}>
                {t('game-zone.in-game')}
              </Text>
              <Text
                color="white"
                fontSize={{ base: '16px', sm: '18px' }}
                fontWeight={700}
                lineHeight={1.2}
                overflowWrap="anywhere"
              >
                {gameBalance.humanFormat} {currencyLabel}
              </Text>
            </VStack>
          </HStack>
        ) : (
          <Menu placement="bottom" matchWidth>
            <MenuButton
              as={Button}
              variant="secondary"
              h="56px"
              px={4}
              bg="ninja.500"
              border="none"
              borderRadius="12px"
              isDisabled={isProcessing}
              _hover={{ bg: 'ninja.500' }}
              _active={{ bg: 'ninja.500' }}
              _disabled={{ opacity: 1, cursor: 'default' }}
            >
              <HStack justify="space-between" spacing={4}>
                <HStack spacing={2} minW={0}>
                  <Box flexShrink={0}>{selectedToken?.icon('24px')}</Box>
                  <Text fontSize="16px" fontWeight={700} lineHeight={1} noOfLines={1}>
                    {tokenName}
                  </Text>
                </HStack>
                <HStack spacing={6} flexShrink={0}>
                  <VStack align="end" spacing={0} w={{ base: 'auto', sm: '110px' }} textAlign="right">
                    <HStack spacing={1}>
                      <Text fontSize="14px" fontWeight={700} color="ninja.100" lineHeight={1}>
                        {availableBalance.humanFormat}
                      </Text>
                      <Text fontSize="14px" fontWeight={700} color="ninja.300" lineHeight={1}>
                        {currencyLabel}
                      </Text>
                    </HStack>
                    {showUsdEquivalent && (
                      <Text fontSize="11px" fontWeight={600} color="ninja.200" lineHeight={1} mt={1}>
                        ≈${formatAmount(toUsd(availableBalance.humanNumber), 2)}
                      </Text>
                    )}
                  </VStack>
                  {mode === 'withdraw' && <ChevronDownIcon width="20px" height="20px" />}
                </HStack>
              </HStack>
            </MenuButton>
            {mode === 'withdraw' && (
              <MenuList
                minW="100%"
                p={2}
                bg="ninja.600"
                border="none"
                borderRadius="12px"
                boxShadow="0 20px 47px rgba(0, 0, 0, 0.25)"
                zIndex={1500}
              >
                {availableCurrencies.map((availableCurrency) => {
                  const token = getTokenByCurrency(availableCurrency)
                  return (
                    <MenuItem
                      key={availableCurrency}
                      gap={3}
                      px={3}
                      py={2.5}
                      bg={availableCurrency === selectedCurrency ? 'ninja.500' : 'transparent'}
                      borderRadius="8px"
                      onClick={() => handleCurrencyChange(availableCurrency)}
                      _hover={{ bg: 'ninja.500' }}
                    >
                      <Box flexShrink={0}>{token?.icon('20px')}</Box>
                      <Text fontSize="14px" fontWeight={700}>
                        {token.name}
                      </Text>
                    </MenuItem>
                  )
                })}
              </MenuList>
            )}
          </Menu>
        )}
      </VStack>

      {mode === 'withdraw' && (
        <HStack h="42px" px={4} py={3} spacing={2} bg="ninja.500" borderRadius="12px">
          <Text flex={1} fontSize="14px" fontWeight={500} color="ninja.200" lineHeight={1}>
            {t('game-zone.current-balance')}
          </Text>
          {showUsdEquivalent && (
            <Text fontSize="14px" fontWeight={700} lineHeight={1}>
              ${formatAmount(toUsd(gameBalance.humanNumber), 2)}
            </Text>
          )}
          <Text fontSize="14px" fontWeight={500} color="ninja.300" lineHeight={1}>
            {gameBalance.humanFormat} {currencyLabel}
          </Text>
        </HStack>
      )}

      <VStack align="stretch" spacing={mode === 'deposit' ? 4 : 1.5}>
        {mode === 'deposit' ? (
          <HStack justify="space-between" align="baseline" flexWrap="wrap" rowGap={1} columnGap={3}>
            <Text color="ninja.100" fontSize="12px" fontWeight={600}>
              {t('game-zone.add-from-wallet')}
            </Text>
            <Text color="ninja.200" fontSize="12px" fontWeight={500} ml="auto" textAlign="right">
              {t('game-zone.available', { amount: walletBalance.humanFormat, currency: currencyLabel })}
            </Text>
          </HStack>
        ) : (
          <Text fontSize="12px" fontWeight={700} lineHeight={1} color="ninja.200">
            {t('game-zone.amount-to-withdraw')}
          </Text>
        )}
        <GameZoneAmountInput
          value={amount}
          onChange={setAmount}
          denom={selectedDenom}
          currency={currencyLabel}
          precision={selectedToken.precisions}
          maxAmount={maxAmount.humanBigNumber}
          isDisabled={isProcessing}
          usdValue={
            mode === 'withdraw' && showUsdEquivalent && amount
              ? `≈$${formatAmount(toUsd(Number(amount)), 2)}`
              : undefined
          }
        />
      </VStack>

      {mode === 'deposit' && otherGameBalances.length > 0 && (
        <GameZoneNotice>{t('game-zone.deposit-notice', { currency: currencyLabel })}</GameZoneNotice>
      )}

      <Button
        size="lg"
        h="56px"
        px={6}
        color="white"
        border="none"
        borderRadius="12px"
        fontSize="16px"
        fontWeight={700}
        isDisabled={!canExecute || !amount}
        onClick={handleExecute}
      >
        {isProcessing ? <Spinner size="sm" /> : title}
      </Button>
    </GameZoneModal>
  )
}
